import React from "react";
import {
  Compass,
  RocketLaunch,
  Users,
  ChartLineUp,
  Megaphone,
  GearSix,
  Sparkle,
  StackSimple,
} from "@phosphor-icons/react";
import { Reveal } from "../lib/motion";
import { Eyebrow, PrimaryButton } from "../components/ui-kit";

const SECTIONS = [
  {
    icon: Compass,
    title: "Business Strategy",
    desc: "Clarity on where the company is going and how it will win — from positioning and priorities to the decision frameworks that keep everyone aligned.",
  },
  {
    icon: RocketLaunch,
    title: "Go-to-Market",
    desc: "A sharp, repeatable path to your customers. We shape the motion, the message, and the milestones that turn early traction into momentum.",
  },
  {
    icon: Users,
    title: "Leadership",
    desc: "Founder coaching and leadership development that grows the people who grow the company — building teams that own outcomes.",
  },
  {
    icon: ChartLineUp,
    title: "Sales",
    desc: "Revenue strategy and sales systems that move you beyond founder-led selling into a predictable, coachable engine.",
  },
  {
    icon: Megaphone,
    title: "Marketing",
    desc: "Brand positioning, demand generation, and content strategy that build trust and pipeline without the noise.",
  },
  {
    icon: GearSix,
    title: "Operations",
    desc: "Business processes, KPIs, and operational excellence — the quiet machinery that lets the company run without you in every room.",
  },
  {
    icon: Sparkle,
    title: "AI Integration",
    desc: "Helping founders leverage AI across functions to improve productivity, decision-making, and customer experience.",
  },
  {
    icon: StackSimple,
    title: "Business Systems",
    desc: "The connective tissue of a scalable company — systems of record, workflows, and playbooks that make growth repeatable.",
  },
];

export default function HowWeHelp() {
  return (
    <div data-testid="how-we-help-page">
      <section className="bg-cloud pt-40 pb-24 lg:pt-48 lg:pb-28">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How We Help</Eyebrow>
            <h1 className="mt-7 max-w-4xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.4rem]">
              Everything it takes to turn a startup into a{" "}
              <span className="font-semibold text-gold">company.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              We work across the full surface of your business — strategy to systems,
              leadership to AI — and go deep where it matters most for your stage.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-8 lg:py-12">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            const even = i % 2 === 1;
            return (
              <Reveal
                key={s.title}
                className={`grid items-center gap-8 border-b border-slate-100 py-14 lg:grid-cols-[auto_1fr_auto] lg:gap-14 ${
                  even ? "" : ""
                }`}
              >
                <div className="font-heading text-5xl font-bold text-slate-200 lg:text-6xl">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="max-w-2xl">
                  <h2 className="font-heading text-3xl font-semibold tracking-tight text-navy">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-lg leading-relaxed text-slate-600">{s.desc}</p>
                </div>
                <div className="grid h-20 w-20 place-items-center rounded-2xl bg-navy/5 text-navy transition-colors duration-300 hover:bg-navy hover:text-gold">
                  <Icon size={36} weight="duotone" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-navy py-24 text-center lg:py-28">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-heading text-4xl font-light tracking-tight text-white sm:text-5xl">
              Not sure where to start? Neither were most founders we work with.
            </h2>
            <div className="mt-9 flex justify-center">
              <PrimaryButton
                to="/contact"
                testid="how-we-help-cta"
                className="bg-gold text-navy hover:bg-gold-light"
              >
                Let's Build Together
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
